import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDeckNavigation from '../hooks/useDeckNavigation';
import { APP_STORE_URL, PLAY_STORE_URL } from '../storeLinks';

function buildSlides(t) {
  return [
    {
      id: 'cover',
      title: t('explainer.titles.cover'),
      render: () => (
        <div className="pitch-slide pitch-cover">
          <div className="pitch-cover-chip">{t('explainer.cover.chip')}</div>
          <img src="/logo512.png" alt="10 Thirty" className="pitch-cover-logo" />
          <h1 className="pitch-cover-title">{t('explainer.cover.title')}</h1>
          <p className="pitch-cover-tagline">{t('explainer.cover.tagline')}</p>
          <p className="pitch-cover-sub">{t('explainer.cover.sub')}</p>
        </div>
      ),
    },

    {
      id: 'problem',
      title: t('explainer.titles.problem'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.problem.label')}</p>
          <h2 className="pitch-heading">{t('explainer.problem.heading')}</h2>
          <div className="pitch-body">
            <p>{t('explainer.problem.p1')}</p>
            <p>{t('explainer.problem.p2')}</p>
          </div>
          <div className="pitch-stat-row">
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('explainer.problem.stat1Num')}</span>
              <span className="pitch-stat-label">{t('explainer.problem.stat1Label')}</span>
            </div>
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('explainer.problem.stat2Num')}</span>
              <span className="pitch-stat-label">{t('explainer.problem.stat2Label')}</span>
            </div>
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('explainer.problem.stat3Num')}</span>
              <span className="pitch-stat-label">{t('explainer.problem.stat3Label')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'tunnel',
      title: t('explainer.titles.tunnel'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.tunnel.label')}</p>
          <h2 className="pitch-heading">{t('explainer.tunnel.heading')}</h2>
          <div className="pitch-body">
            <p>{t('explainer.tunnel.intro')}</p>
          </div>
          <div className="pitch-features">
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🤝</span>
              <span className="pitch-feature-title">{t('explainer.tunnel.feat1Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.tunnel.feat1Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">⌛</span>
              <span className="pitch-feature-title">{t('explainer.tunnel.feat2Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.tunnel.feat2Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🔐</span>
              <span className="pitch-feature-title">{t('explainer.tunnel.feat3Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.tunnel.feat3Desc')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'walkthrough',
      title: t('explainer.titles.walkthrough'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.walkthrough.label')}</p>
          <h2 className="pitch-heading">{t('explainer.walkthrough.heading')}</h2>
          <div className="pitch-how-it-works">
            <div className="pitch-step">
              <span className="pitch-step-num">01</span>
              <span className="pitch-step-text">{t('explainer.walkthrough.step1')}</span>
            </div>
            <div className="pitch-step-arrow">&rarr;</div>
            <div className="pitch-step">
              <span className="pitch-step-num">02</span>
              <span className="pitch-step-text">{t('explainer.walkthrough.step2')}</span>
            </div>
            <div className="pitch-step-arrow">&rarr;</div>
            <div className="pitch-step">
              <span className="pitch-step-num">03</span>
              <span className="pitch-step-text">{t('explainer.walkthrough.step3')}</span>
            </div>
            <div className="pitch-step-arrow">&rarr;</div>
            <div className="pitch-step">
              <span className="pitch-step-num">04</span>
              <span className="pitch-step-text">{t('explainer.walkthrough.step4')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'timing',
      title: t('explainer.titles.timing'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.timing.label')}</p>
          <h2 className="pitch-heading">{t('explainer.timing.heading')}</h2>
          <div className="pitch-kpi-grid">
            <div className="pitch-kpi pitch-kpi-highlight">
              <span className="pitch-kpi-label">{t('explainer.timing.tunnelLabel')}</span>
              <strong className="pitch-kpi-value">{t('explainer.timing.tunnelValue')}</strong>
              <span className="pitch-kpi-copy">{t('explainer.timing.tunnelCopy')}</span>
            </div>
            <div className="pitch-kpi pitch-kpi-highlight">
              <span className="pitch-kpi-label">{t('explainer.timing.msgLabel')}</span>
              <strong className="pitch-kpi-value">{t('explainer.timing.msgValue')}</strong>
              <span className="pitch-kpi-copy">{t('explainer.timing.msgCopy')}</span>
            </div>
          </div>
          <div className="pitch-body pitch-body-small">
            <p>{t('explainer.timing.body1')}</p>
            <p>{t('explainer.timing.body2')}</p>
          </div>
        </div>
      ),
    },

    {
      id: 'encryption',
      title: t('explainer.titles.encryption'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.encryption.label')}</p>
          <h2 className="pitch-heading">{t('explainer.encryption.heading')}</h2>
          <div className="pitch-body">
            <p>{t('explainer.encryption.body')}</p>
          </div>
          <div className="pitch-stat-row">
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('explainer.encryption.stat1Num')}</span>
              <span className="pitch-stat-label">{t('explainer.encryption.stat1Label')}</span>
            </div>
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('explainer.encryption.stat2Num')}</span>
              <span className="pitch-stat-label">{t('explainer.encryption.stat2Label')}</span>
            </div>
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('explainer.encryption.stat3Num')}</span>
              <span className="pitch-stat-label">{t('explainer.encryption.stat3Label')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'sos',
      title: t('explainer.titles.sos'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.sos.label')}</p>
          <h2 className="pitch-heading">{t('explainer.sos.heading')}</h2>
          <div className="pitch-body">
            <p>{t('explainer.sos.body')}</p>
          </div>
          <div className="pitch-features">
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🛡️</span>
              <span className="pitch-feature-title">{t('explainer.sos.feat1Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.sos.feat1Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">📍</span>
              <span className="pitch-feature-title">{t('explainer.sos.feat2Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.sos.feat2Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">✅</span>
              <span className="pitch-feature-title">{t('explainer.sos.feat3Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.sos.feat3Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🆓</span>
              <span className="pitch-feature-title">{t('explainer.sos.feat4Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.sos.feat4Desc')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'pigeons',
      title: t('explainer.titles.pigeons'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.pigeons.label')}</p>
          <h2 className="pitch-heading">{t('explainer.pigeons.heading')}</h2>
          <div className="pitch-body">
            <p>{t('explainer.pigeons.body')}</p>
          </div>
          <div className="pitch-features">
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🪺</span>
              <span className="pitch-feature-title">{t('explainer.pigeons.feat1Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.pigeons.feat1Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🕊️</span>
              <span className="pitch-feature-title">{t('explainer.pigeons.feat2Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.pigeons.feat2Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🧹</span>
              <span className="pitch-feature-title">{t('explainer.pigeons.feat3Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.pigeons.feat3Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🤝</span>
              <span className="pitch-feature-title">{t('explainer.pigeons.feat4Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.pigeons.feat4Desc')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'safety',
      title: t('explainer.titles.safety'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('explainer.safety.label')}</p>
          <h2 className="pitch-heading">{t('explainer.safety.heading')}</h2>
          <div className="pitch-features">
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🚫</span>
              <span className="pitch-feature-title">{t('explainer.safety.feat1Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.safety.feat1Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">⏱️</span>
              <span className="pitch-feature-title">{t('explainer.safety.feat2Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.safety.feat2Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">📵</span>
              <span className="pitch-feature-title">{t('explainer.safety.feat3Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.safety.feat3Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🔑</span>
              <span className="pitch-feature-title">{t('explainer.safety.feat4Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.safety.feat4Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">👆</span>
              <span className="pitch-feature-title">{t('explainer.safety.feat5Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.safety.feat5Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">⚠️</span>
              <span className="pitch-feature-title">{t('explainer.safety.feat6Title')}</span>
              <span className="pitch-feature-desc">{t('explainer.safety.feat6Desc')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'cta',
      title: t('explainer.titles.cta'),
      render: () => (
        <div className="pitch-slide pitch-content explainer-cta">
          <p className="pitch-label">{t('explainer.cta.label')}</p>
          <h2 className="pitch-heading">{t('explainer.cta.heading')}</h2>
          <div className="pitch-tiers">
            <div className="pitch-tier">
              <p className="pitch-tier-name">{t('explainer.cta.tier1Name')}</p>
              <p className="pitch-tier-desc">{t('explainer.cta.tier1Desc')}</p>
            </div>
            <div className="pitch-tier pitch-tier-highlight">
              <p className="pitch-tier-name">{t('explainer.cta.tier2Name')}</p>
              <p className="pitch-tier-desc">{t('explainer.cta.tier2Desc')}</p>
            </div>
            <div className="pitch-tier">
              <p className="pitch-tier-name">{t('explainer.cta.tier3Name')}</p>
              <p className="pitch-tier-desc">{t('explainer.cta.tier3Desc')}</p>
            </div>
          </div>
          <div className="explainer-store-row">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="explainer-store-btn">
              {t('explainer.cta.appStoreCta')}
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="explainer-store-btn">
              {t('explainer.cta.playStoreCta')}
            </a>
          </div>
          <div className="pitch-contact">
            <p>{t('explainer.cta.contact')}</p>
          </div>
        </div>
      ),
    },
  ];
}

export default function Explainer() {
  const { t } = useTranslation();
  const slides = buildSlides(t);
  const total = slides.length;
  const { current, prev, next, stageProps } = useDeckNavigation(total);

  const slide = slides[current];

  return (
    <div className="pitch-deck">
      <div className="pitch-stage" {...stageProps}>
        {slide.render()}
      </div>

      <div className="pitch-controls">
        <Link to="/" className="pitch-home-link" aria-label={t('explainer.controls.backToSite')}>
          <img src="/logo192.png" alt="10 Thirty" className="pitch-home-logo" />
        </Link>
        <button
          className="pitch-nav-btn"
          onClick={prev}
          disabled={current === 0}
          aria-label={t('explainer.controls.prevSlide')}
        >
          &larr;
        </button>
        <div className="pitch-counter-wrap">
          <span className="pitch-counter-label">{slide.title}</span>
          <span className="pitch-counter">{current + 1} / {total}</span>
        </div>
        <button
          className="pitch-nav-btn"
          onClick={next}
          disabled={current === total - 1}
          aria-label={t('explainer.controls.nextSlide')}
        >
          &rarr;
        </button>
      </div>

      <div className="pitch-progress-track">
        <div
          className="pitch-progress-fill"
          style={{ width: `${((current + 1) / total) * 100}%` }}
        />
      </div>
    </div>
  );
}
